import { createChessArticle, ChessArticle } from '../services/chesspediaService';

/**
 * Generate rich chess educational content with images
 */
export const generateSampleChessArticles = (): Omit<ChessArticle, 'id' | 'createdAt' | 'updatedAt'>[] => {
    return [
        {
            title: 'Introduction to Chess: The Royal Game',
            category: 'basics',
            content: `
                <div style="max-width: 800px; margin: 0 auto; line-height: 1.8;">
                    <h2 style="color: #1f2937; font-size: 2rem; margin-bottom: 1rem; border-bottom: 3px solid #9333ea; padding-bottom: 0.5rem;">Welcome to the World of Chess!</h2>
                    <p style="font-size: 1.1rem; color: #374151; margin-bottom: 1.5rem;">
                        Chess is a two-player strategy board game played on a checkered board with 64 squares arranged in an 8×8 grid. 
                        Known as the "Royal Game," chess has been played for over 1,500 years and is enjoyed by millions worldwide.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">The Chessboard</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        The chessboard is an 8×8 grid of alternating light and dark squares. Always set up the board so that 
                        a light square is in the bottom-right corner. Each player starts with 16 pieces:
                    </p>
                    <ul style="list-style: none; padding: 0; margin: 1rem 0;">
                        <li style="padding: 0.5rem; background: #f3f4f6; margin: 0.25rem 0; border-radius: 0.25rem;">👑 <strong>1 King</strong> - The most important piece</li>
                        <li style="padding: 0.5rem; background: #f3f4f6; margin: 0.25rem 0; border-radius: 0.25rem;">♛ <strong>1 Queen</strong> - The most powerful piece</li>
                        <li style="padding: 0.5rem; background: #f3f4f6; margin: 0.25rem 0; border-radius: 0.25rem;">♜ <strong>2 Rooks</strong> - Move in straight lines</li>
                        <li style="padding: 0.5rem; background: #f3f4f6; margin: 0.25rem 0; border-radius: 0.25rem;">♝ <strong>2 Bishops</strong> - Move diagonally</li>
                        <li style="padding: 0.5rem; background: #f3f4f6; margin: 0.25rem 0; border-radius: 0.25rem;">♞ <strong>2 Knights</strong> - Move in L-shapes</li>
                        <li style="padding: 0.5rem; background: #f3f4f6; margin: 0.25rem 0; border-radius: 0.25rem;">♟ <strong>8 Pawns</strong> - The foot soldiers</li>
                    </ul>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">The Goal of Chess</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        The goal is to <strong>checkmate</strong> your opponent's king. This means the king is in a position to be captured (in check) 
                        and cannot escape. Checkmate ends the game immediately.
                    </p>
                    
                    <div style="background: #fef3c7; border-left: 4px solid #f59e0b; padding: 1rem; margin: 1.5rem 0; border-radius: 0.5rem;">
                        <strong>💡 Pro Tip:</strong> Always protect your king! A checkmated king means the game is over, regardless of how many pieces you have.
                    </div>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Understanding Piece Movement</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">Each piece has unique movement rules:</p>
                    <div style="background: #f9fafb; padding: 1.5rem; border-radius: 0.75rem; margin: 1rem 0;">
                        <p style="margin: 0.75rem 0;"><strong>♟ Pawn:</strong> Moves forward one square (two squares on first move), captures diagonally. Can promote to any piece when reaching the 8th rank.</p>
                        <p style="margin: 0.75rem 0;"><strong>♜ Rook:</strong> Moves horizontally or vertically any number of squares. Powerful in open positions.</p>
                        <p style="margin: 0.75rem 0;"><strong>♞ Knight:</strong> Moves in an L-shape (2 squares in one direction, 1 square perpendicular). Can jump over pieces!</p>
                        <p style="margin: 0.75rem 0;"><strong>♝ Bishop:</strong> Moves diagonally any number of squares. Each player has one light-squared and one dark-squared bishop.</p>
                        <p style="margin: 0.75rem 0;"><strong>♛ Queen:</strong> Combines the power of rook and bishop. Moves any number of squares in any direction.</p>
                        <p style="margin: 0.75rem 0;"><strong>👑 King:</strong> Moves one square in any direction. Can castle with a rook under certain conditions.</p>
                    </div>
                </div>
            `,
            images: [], // Images should be uploaded to Firebase Storage
            youtubeLinks: ['https://youtu.be/r6b2zchefvU'],
            authorId: 'system',
            authorName: 'Chess Academy',
            tags: ['introduction', 'rules', 'basics', 'pieces', 'movement'],
            difficulty: 'beginner'
        },
        {
            title: 'Mastering the Opening: Control the Center',
            category: 'openings',
            content: `
                <div style="max-width: 800px; margin: 0 auto; line-height: 1.8;">
                    <h2 style="color: #1f2937; font-size: 2rem; margin-bottom: 1rem; border-bottom: 3px solid #9333ea; padding-bottom: 0.5rem;">Opening Principles for Success</h2>
                    <p style="font-size: 1.1rem; color: #374151; margin-bottom: 1.5rem;">
                        The opening is the first phase of the game (roughly moves 1-15). Following these fundamental principles 
                        will give you a strong position and set you up for success in the middlegame.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">1. Control the Center</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        The center squares (e4, e5, d4, d5) are the most important squares on the board. Pieces placed in the center 
                        have more mobility and can influence more squares. Control of the center gives you space and flexibility.
                    </p>
                    <div style="background: #dbeafe; padding: 1rem; border-radius: 0.5rem; margin: 1rem 0;">
                        <strong>Example:</strong> 1.e4 e5 (both players control central squares) is one of the most common opening moves.
                    </div>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">2. Develop Your Pieces</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        Bring your knights and bishops into the game quickly. Develop toward the center when possible. 
                        <strong>Don't move the same piece multiple times</strong> in the opening unless there's a good reason.
                    </p>
                    <ul style="color: #374151; margin: 1rem 0; padding-left: 1.5rem;">
                        <li>Develop knights before bishops (knights are usually developed first)</li>
                        <li>Don't bring out the queen too early - it can be attacked</li>
                        <li>Connect your rooks by moving pieces in between</li>
                    </ul>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">3. Castle Early</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        Castling protects your king and connects your rooks. Try to castle within the first 10 moves. 
                        Kingside castling (0-0) is more common and usually safer.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">4. Don't Bring Out the Queen Too Early</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        The queen is powerful but vulnerable. Develop minor pieces (knights and bishops) first. 
                        Bringing the queen out early often leads to it being chased around by your opponent's pieces, wasting time.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Popular Opening Systems</h3>
                    <div style="background: #f9fafb; padding: 1.5rem; border-radius: 0.75rem; margin: 1rem 0;">
                        <p style="margin: 0.75rem 0;"><strong>Italian Game:</strong> 1.e4 e5 2.Nf3 Nc6 3.Bc4 - Classic, aggressive opening</p>
                        <p style="margin: 0.75rem 0;"><strong>Sicilian Defense:</strong> 1.e4 c5 - Most popular response to 1.e4</p>
                        <p style="margin: 0.75rem 0;"><strong>French Defense:</strong> 1.e4 e6 - Solid, positional opening</p>
                        <p style="margin: 0.75rem 0;"><strong>Queen's Gambit:</strong> 1.d4 d5 2.c4 - Popular in professional chess</p>
                        <p style="margin: 0.75rem 0;"><strong>Ruy Lopez:</strong> 1.e4 e5 2.Nf3 Nc6 3.Bb5 - The "Spanish Opening"</p>
                    </div>
                    
                    <div style="background: #dcfce7; border-left: 4px solid #10b981; padding: 1rem; margin: 1.5rem 0; border-radius: 0.5rem;">
                        <strong>✅ Remember:</strong> Understanding opening principles is more important than memorizing specific moves. 
                        Focus on controlling the center, developing pieces, and keeping your king safe.
                    </div>
                </div>
            `,
            images: [], // Images should be uploaded to Firebase Storage
            youtubeLinks: ['https://youtube.com/shorts/YS8WKYH7FYI'],
            authorId: 'system',
            authorName: 'Chess Academy',
            tags: ['opening', 'strategy', 'center', 'development', 'principles'],
            difficulty: 'beginner'
        },
        {
            title: 'Castling: The Special King Move',
            category: 'castling',
            content: `
                <div style="max-width: 800px; margin: 0 auto; line-height: 1.8;">
                    <h2 style="color: #1f2937; font-size: 2rem; margin-bottom: 1rem; border-bottom: 3px solid #9333ea; padding-bottom: 0.5rem;">Understanding Castling</h2>
                    <p style="font-size: 1.1rem; color: #374151; margin-bottom: 1.5rem;">
                        Castling is a special move that involves both the king and one rook. It's the only move in chess where 
                        two pieces move at once, and it's one of the most important moves in the opening.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">When Can You Castle?</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">You can castle if ALL of these conditions are met:</p>
                    <ul style="color: #374151; margin: 1rem 0; padding-left: 1.5rem;">
                        <li>Neither the king nor the rook has moved from its starting position</li>
                        <li>There are no pieces between the king and rook</li>
                        <li>The king is not currently in check</li>
                        <li>The king does not pass through a square that is under attack</li>
                        <li>The king does not end up in check</li>
                    </ul>
                    
                    <div style="background: #fee2e2; border-left: 4px solid #ef4444; padding: 1rem; margin: 1.5rem 0; border-radius: 0.5rem;">
                        <strong>⚠️ Important:</strong> If you've moved your king, you can never castle for the rest of the game, even if you move it back to its original square.
                    </div>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Types of Castling</h3>
                    
                    <div style="background: #f9fafb; padding: 1.5rem; border-radius: 0.75rem; margin: 1rem 0;">
                        <h4 style="color: #1f2937; margin-bottom: 0.75rem;">Kingside Castling (Short Castling) - 0-0</h4>
                        <p style="color: #374151; margin-bottom: 0.5rem;">
                            The king moves two squares toward the rook on the kingside (right side for White, left side for Black), 
                            and the rook moves to the square next to the king on the other side.
                        </p>
                        <p style="color: #6b7280; font-size: 0.9rem; font-style: italic;">
                            Example: White castles kingside: King from e1 to g1, Rook from h1 to f1
                        </p>
                    </div>
                    
                    <div style="background: #f9fafb; padding: 1.5rem; border-radius: 0.75rem; margin: 1rem 0;">
                        <h4 style="color: #1f2937; margin-bottom: 0.75rem;">Queenside Castling (Long Castling) - 0-0-0</h4>
                        <p style="color: #374151; margin-bottom: 0.5rem;">
                            The king moves two squares toward the rook on the queenside (left side for White, right side for Black), 
                            and the rook moves to the square next to the king on the other side.
                        </p>
                        <p style="color: #6b7280; font-size: 0.9rem; font-style: italic;">
                            Example: White castles queenside: King from e1 to c1, Rook from a1 to d1
                        </p>
                    </div>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Why Castle?</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">Castling serves two crucial purposes:</p>
                    <ol style="color: #374151; margin: 1rem 0; padding-left: 1.5rem;">
                        <li style="margin: 0.5rem 0;"><strong>King Safety:</strong> Moves the king to a safer position, usually behind a wall of pawns</li>
                        <li style="margin: 0.5rem 0;"><strong>Rook Activation:</strong> Brings the rook into the game and connects your rooks</li>
                    </ol>
                    
                    <div style="background: #dbeafe; border-left: 4px solid #3b82f6; padding: 1rem; margin: 1.5rem 0; border-radius: 0.5rem;">
                        <strong>💡 Strategy:</strong> In most games, players castle kingside. Queenside castling is less common but can be useful 
                        in certain openings like the Sicilian Defense or when you want to launch a queenside attack.
                    </div>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Common Castling Patterns</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        After castling, your king is usually safer, but be careful not to weaken the pawns in front of your castled king. 
                        Moving pawns in front of a castled king (like h3, g3, or f3) can create weaknesses that your opponent can exploit.
                    </p>
                </div>
            `,
            images: [], // Images should be uploaded to Firebase Storage
            youtubeLinks: ['https://youtube.com/shorts/2OgrtOe9Zrc'],
            authorId: 'system',
            authorName: 'Chess Academy',
            tags: ['castling', 'king safety', 'rules', 'opening'],
            difficulty: 'beginner'
        },
        {
            title: 'Defensive Mastery: Protecting Your Position',
            category: 'defense',
            content: `
                <div style="max-width: 800px; margin: 0 auto; line-height: 1.8;">
                    <h2 style="color: #1f2937; font-size: 2rem; margin-bottom: 1rem; border-bottom: 3px solid #9333ea; padding-bottom: 0.5rem;">Defensive Principles</h2>
                    <p style="font-size: 1.1rem; color: #374151; margin-bottom: 1.5rem;">
                        Good defense is just as important as good offense in chess. Many games are won or lost based on defensive skills. 
                        Here are the key defensive concepts every player should master.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">1. Don't Leave Pieces Undefended</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        Every piece should be protected by another piece or pawn. Undefended pieces (called "hanging" pieces) are easy targets 
                        for your opponent. Always check if your pieces are safe before making a move.
                    </p>
                    <div style="background: #fee2e2; border-left: 4px solid #ef4444; padding: 1rem; margin: 1.5rem 0; border-radius: 0.5rem;">
                        <strong>⚠️ Common Mistake:</strong> Leaving a piece undefended is one of the most common ways to lose material. 
                        Always count attackers and defenders before moving.
                    </div>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">2. Create Safe Squares</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        When your pieces are under attack, look for safe squares where they can retreat. Sometimes moving a piece to safety 
                        is better than defending it. A piece that can't be attacked is better than a defended piece.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">3. Counterattack</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        The best defense is often a good offense. If your opponent attacks, look for ways to counterattack their pieces. 
                        This forces them to defend, giving you time to improve your position.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">4. Defensive Tactics</h3>
                    <div style="background: #f9fafb; padding: 1.5rem; border-radius: 0.75rem; margin: 1rem 0;">
                        <p style="margin: 0.75rem 0;"><strong>Blocking:</strong> Place a piece between the attacker and your piece. This is often the simplest defense.</p>
                        <p style="margin: 0.75rem 0;"><strong>Interposing:</strong> Move a piece to block a check. This is the only way to defend against a check (besides moving the king or capturing).</p>
                        <p style="margin: 0.75rem 0;"><strong>Pinning:</strong> Pin your opponent's attacking piece to something more valuable. If they move it, they lose material.</p>
                        <p style="margin: 0.75rem 0;"><strong>Counter-pinning:</strong> Pin the piece that's attacking you. This can neutralize the threat.</p>
                        <p style="margin: 0.75rem 0;"><strong>Overloading:</strong> Attack a piece that's defending multiple things. If it moves, something else becomes vulnerable.</p>
                    </div>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">5. King Safety</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        Always keep your king safe. Castle early, keep pawns in front of your castled king, and avoid moving pawns that protect your king. 
                        An exposed king is vulnerable to checkmate threats.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Common Defensive Mistakes</h3>
                    <ul style="color: #374151; margin: 1rem 0; padding-left: 1.5rem;">
                        <li>Moving pawns in front of a castled king (creates weaknesses)</li>
                        <li>Leaving pieces hanging (undefended)</li>
                        <li>Not seeing threats (always look at what your opponent can do)</li>
                        <li>Defending with the queen (too valuable, can be attacked)</li>
                        <li>Over-defending (using too many pieces to defend one piece)</li>
                    </ul>
                    
                    <div style="background: #dcfce7; border-left: 4px solid #10b981; padding: 1rem; margin: 1.5rem 0; border-radius: 0.5rem;">
                        <strong>✅ Pro Tip:</strong> Before making any move, ask yourself: "What can my opponent do after this move?" 
                        This simple question will help you avoid many defensive mistakes.
                    </div>
                </div>
            `,
            images: [], // Images should be uploaded to Firebase Storage
            youtubeLinks: ['https://youtube.com/shorts/5ytdh2z0ZE0'], // Add YouTube video URLs here - Search "chess defense strategy" on YouTube and add video URLs
            authorId: 'system',
            authorName: 'Chess Academy',
            tags: ['defense', 'strategy', 'tactics', 'protection', 'safety'],
            difficulty: 'intermediate'
        },
        {
            title: 'Pawn Structure: The Foundation of Positional Play',
            category: 'strategy',
            content: `
                <div style="max-width: 800px; margin: 0 auto; line-height: 1.8;">
                    <h2 style="color: #1f2937; font-size: 2rem; margin-bottom: 1rem; border-bottom: 3px solid #9333ea; padding-bottom: 0.5rem;">Understanding Pawn Structure</h2>
                    <p style="font-size: 1.1rem; color: #374151; margin-bottom: 1.5rem;">
                        Pawn structure is the arrangement of pawns on the board. It's often called the "skeleton" of the position because 
                        it determines the character of the game and influences piece placement for many moves to come.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Types of Pawn Structures</h3>
                    
                    <div style="background: #f9fafb; padding: 1.5rem; border-radius: 0.75rem; margin: 1rem 0;">
                        <h4 style="color: #1f2937; margin-bottom: 0.75rem;">Isolated Pawns</h4>
                        <p style="color: #374151; margin-bottom: 0.5rem;">
                            A pawn with no friendly pawns on adjacent files. Isolated pawns are weak because they can't be defended by other pawns, 
                            but they can be mobile and control important squares.
                        </p>
                        <p style="color: #6b7280; font-size: 0.9rem; font-style: italic;">
                            Example: A pawn on d4 with no pawns on c4 or e4 is isolated.
                        </p>
                    </div>
                    
                    <div style="background: #f9fafb; padding: 1.5rem; border-radius: 0.75rem; margin: 1rem 0;">
                        <h4 style="color: #1f2937; margin-bottom: 0.75rem;">Doubled Pawns</h4>
                        <p style="color: #374151; margin-bottom: 0.5rem;">
                            Two pawns of the same color on the same file. Doubled pawns are usually weak because they can't defend each other, 
                            but they can control squares and sometimes be useful.
                        </p>
                    </div>
                    
                    <div style="background: #f9fafb; padding: 1.5rem; border-radius: 0.75rem; margin: 1rem 0;">
                        <h4 style="color: #1f2937; margin-bottom: 0.75rem;">Passed Pawn</h4>
                        <p style="color: #374151; margin-bottom: 0.5rem;">
                            A pawn with no enemy pawns in front of it on the same file or adjacent files. Passed pawns are very strong, 
                            especially in the endgame, because they can advance toward promotion.
                        </p>
                        <p style="color: #10b981; font-weight: 600;">This is often the key to winning endgames!</p>
                    </div>
                    
                    <div style="background: #f9fafb; padding: 1.5rem; border-radius: 0.75rem; margin: 1rem 0;">
                        <h4 style="color: #1f2937; margin-bottom: 0.75rem;">Pawn Chains</h4>
                        <p style="color: #374151; margin-bottom: 0.5rem;">
                            Connected pawns that support each other diagonally. Pawn chains are strong defensive structures. 
                            The base of the chain (the rearmost pawn) is the weakest point.
                        </p>
                    </div>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Weaknesses to Avoid</h3>
                    <ul style="color: #374151; margin: 1rem 0; padding-left: 1.5rem;">
                        <li><strong>Backward pawns:</strong> Can't be defended by other pawns, often become targets</li>
                        <li><strong>Holes:</strong> Squares that can't be controlled by pawns, perfect for enemy pieces</li>
                        <li><strong>Doubled pawns:</strong> Usually weak, especially if isolated</li>
                        <li><strong>Isolated pawns:</strong> Can't be defended by pawns, need piece support</li>
                    </ul>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Strengths to Create</h3>
                    <ul style="color: #374151; margin: 1rem 0; padding-left: 1.5rem;">
                        <li><strong>Pawn chains:</strong> Strong defensive structures</li>
                        <li><strong>Central pawns:</strong> Control important squares</li>
                        <li><strong>Passed pawns:</strong> Can promote to a queen</li>
                        <li><strong>Pawn majorities:</strong> More pawns on one side can create a passed pawn</li>
                        <li><strong>Connected pawns:</strong> Can defend each other</li>
                    </ul>
                    
                    <div style="background: #dbeafe; border-left: 4px solid #3b82f6; padding: 1rem; margin: 1.5rem 0; border-radius: 0.5rem;">
                        <strong>💡 Strategy:</strong> In the endgame, pawn structure becomes even more important. A single passed pawn can often 
                        decide the game. Always think about pawn structure when making pawn moves - they can't move backward!
                    </div>
                </div>
            `,
            images: [], // Images should be uploaded to Firebase Storage
            youtubeLinks: ['https://youtube.com/shorts/Zk0V41KaUAk'], // Add YouTube video URLs here - Search "chess pawn structure" on YouTube and add video URLs
            authorId: 'system',
            authorName: 'Chess Academy',
            tags: ['strategy', 'pawns', 'positional', 'structure', 'endgame'],
            difficulty: 'intermediate'
        },
        {
            title: 'Checkmate Patterns: Finishing the Game',
            category: 'basics',
            content: `
                <div style="max-width: 800px; margin: 0 auto; line-height: 1.8;">
                    <h2 style="color: #1f2937; font-size: 2rem; margin-bottom: 1rem; border-bottom: 3px solid #9333ea; padding-bottom: 0.5rem;">Common Checkmate Patterns</h2>
                    <p style="font-size: 1.1rem; color: #374151; margin-bottom: 1.5rem;">
                        Recognizing checkmate patterns is crucial for finishing games. These patterns appear frequently in actual play. 
                        Learning to spot them will help you win more games!
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">1. Back Rank Mate</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        When the king is trapped on the back rank (1st rank for White, 8th rank for Black) by its own pieces, 
                        a rook or queen can deliver checkmate along that rank.
                    </p>
                    <div style="background: #fef3c7; padding: 1rem; border-radius: 0.5rem; margin: 1rem 0;">
                        <strong>Pattern:</strong> King on back rank, blocked by own pawns, enemy rook/queen on the back rank = Checkmate!
                    </div>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">2. Scholar's Mate</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        A quick checkmate in 4 moves: <strong>1.e4 e5 2.Bc4 Nc6 3.Qf3 Nf6 4.Qxf7#</strong>
                    </p>
                    <p style="color: #6b7280; font-size: 0.9rem; font-style: italic;">
                        This works by attacking the weak f7 square with the queen and bishop. It's a trap that catches beginners, 
                        but experienced players know how to defend against it.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">3. Fool's Mate</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        The fastest possible checkmate: <strong>1.f3 e5 2.g4 Qh4#</strong>
                    </p>
                    <p style="color: #6b7280; font-size: 0.9rem; font-style: italic;">
                        This only happens if White makes terrible moves. It's called "Fool's Mate" because you'd have to play like a fool to allow it!
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">4. Smothered Mate</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        A knight checkmates a king that is completely surrounded by its own pieces. The king has no escape squares 
                        and is "smothered" by friendly pieces.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">5. Anastasia's Mate</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        A rook and knight combine to checkmate a king trapped by its own pieces. The rook delivers the checkmate 
                        while the knight prevents the king from escaping.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">6. Arabian Mate</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        A rook and knight work together to checkmate the king. The knight is adjacent to the enemy king, 
                        and the rook delivers checkmate from a distance.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">How to Avoid Checkmate</h3>
                    <ul style="color: #374151; margin: 1rem 0; padding-left: 1.5rem;">
                        <li>Always have an escape square (luft) for your king</li>
                        <li>Don't trap your king with your own pieces</li>
                        <li>Watch for back rank weaknesses</li>
                        <li>Keep your king safe, especially in the opening</li>
                        <li>Don't move pawns in front of a castled king unnecessarily</li>
                    </ul>
                    
                    <div style="background: #dcfce7; border-left: 4px solid #10b981; padding: 1rem; margin: 1.5rem 0; border-radius: 0.5rem;">
                        <strong>✅ Practice:</strong> Study these patterns and practice recognizing them. The more patterns you know, 
                        the faster you'll spot checkmate opportunities in your games!
                    </div>
                </div>
            `,
            images: [], // Images should be uploaded to Firebase Storage
            youtubeLinks: ['https://youtube.com/shorts/MqKrUw9WNXc'], // Add YouTube video URLs here - Search "chess checkmate patterns" on YouTube and add video URLs
            authorId: 'system',
            authorName: 'Chess Academy',
            tags: ['checkmate', 'tactics', 'patterns', 'endgame'],
            difficulty: 'beginner'
        },
        {
            title: 'Tactical Mastery: Forks, Pins, and Skewers',
            category: 'tactics',
            content: `
                <div style="max-width: 800px; margin: 0 auto; line-height: 1.8;">
                    <h2 style="color: #1f2937; font-size: 2rem; margin-bottom: 1rem; border-bottom: 3px solid #9333ea; padding-bottom: 0.5rem;">Essential Tactical Patterns</h2>
                    <p style="font-size: 1.1rem; color: #374151; margin-bottom: 1.5rem;">
                        Tactics are short-term sequences of moves that gain an advantage. They are the building blocks of chess combinations. 
                        Master these patterns to dramatically improve your game!
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">1. Fork</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        A fork occurs when one piece attacks two or more enemy pieces simultaneously. The attacking piece forces the opponent 
                        to choose which piece to save, usually resulting in material gain.
                    </p>
                    <div style="background: #f9fafb; padding: 1.5rem; border-radius: 0.75rem; margin: 1rem 0;">
                        <p style="margin: 0.75rem 0;"><strong>Knight Forks:</strong> Knights are especially good at forking because of their unique L-shaped movement. 
                        A knight on e5 can fork the king and rook, or two rooks, or a queen and rook.</p>
                        <p style="margin: 0.75rem 0;"><strong>Pawn Forks:</strong> Pawns can fork pieces diagonally. A pawn on d4 can attack pieces on c5 and e5.</p>
                        <p style="margin: 0.75rem 0;"><strong>Queen Forks:</strong> The queen can fork multiple pieces due to its mobility.</p>
                    </div>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">2. Pin</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        A pin occurs when a piece cannot move without exposing a more valuable piece behind it. The pinned piece is "stuck" 
                        because moving it would be worse than leaving it where it is.
                    </p>
                    <div style="background: #f9fafb; padding: 1.5rem; border-radius: 0.75rem; margin: 1rem 0;">
                        <p style="margin: 0.75rem 0;"><strong>Absolute Pin:</strong> A piece pinned to the king. The pinned piece cannot move at all (it would be illegal).</p>
                        <p style="margin: 0.75rem 0;"><strong>Relative Pin:</strong> A piece pinned to a more valuable piece (but not the king). The pinned piece can move, but it's usually bad.</p>
                        <p style="margin: 0.75rem 0;"><strong>Example:</strong> A bishop pins a knight to the king. The knight cannot move because it would expose the king to check.</p>
                    </div>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">3. Skewer</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        Similar to a pin, but the more valuable piece is in front. When the valuable piece moves to safety, 
                        the less valuable piece behind it is captured.
                    </p>
                    <p style="color: #6b7280; font-size: 0.9rem; font-style: italic; margin: 1rem 0;">
                        Example: A rook attacks a queen, which must move, exposing a rook behind it that can then be captured.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">4. Discovered Attack</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        When moving one piece reveals an attack by another piece behind it. This is a powerful tactic because 
                        the moving piece can also create a threat, creating a double attack.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">5. Double Attack</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        Attacking two pieces at once, similar to a fork but can involve different pieces working together. 
                        The opponent can only defend one threat.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">How to Use Tactics</h3>
                    <ul style="color: #374151; margin: 1rem 0; padding-left: 1.5rem;">
                        <li>Look for undefended pieces (hanging pieces)</li>
                        <li>Find pieces that are overloaded (defending too many things)</li>
                        <li>Look for tactical opportunities after every move</li>
                        <li>Practice tactics regularly to improve pattern recognition</li>
                        <li>Calculate forcing moves first (checks, captures, threats)</li>
                    </ul>
                    
                    <div style="background: #dbeafe; border-left: 4px solid #3b82f6; padding: 1rem; margin: 1.5rem 0; border-radius: 0.5rem;">
                        <strong>💡 Training:</strong> Solving tactical puzzles daily is one of the fastest ways to improve your chess. 
                        The more patterns you recognize, the easier it becomes to spot tactics in your games.
                    </div>
                </div>
            `,
            images: [], // Images should be uploaded to Firebase Storage
            youtubeLinks: ['https://youtube.com/shorts/j-5EG2QIf60'], // Add YouTube video URLs here - Search "chess tactics forks pins skewers" on YouTube and add video URLs
            authorId: 'system',
            authorName: 'Chess Academy',
            tags: ['tactics', 'fork', 'pin', 'skewer', 'patterns'],
            difficulty: 'intermediate'
        },
        {
            title: 'Endgame Fundamentals: King Activity and Pawn Promotion',
            category: 'endgame',
            content: `
                <div style="max-width: 800px; margin: 0 auto; line-height: 1.8;">
                    <h2 style="color: #1f2937; font-size: 2rem; margin-bottom: 1rem; border-bottom: 3px solid #9333ea; padding-bottom: 0.5rem;">Mastering the Endgame</h2>
                    <p style="font-size: 1.1rem; color: #374151; margin-bottom: 1.5rem;">
                        The endgame begins when most pieces have been exchanged. This phase requires different skills than the opening or middlegame. 
                        Endgame technique is often what separates good players from great ones.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">King Activity</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        In the endgame, the king becomes a powerful piece! Unlike in the opening and middlegame where the king needs protection, 
                        in the endgame you should activate your king and use it to support your pawns and attack enemy pawns.
                    </p>
                    <div style="background: #dcfce7; border-left: 4px solid #10b981; padding: 1rem; margin: 1.5rem 0; border-radius: 0.5rem;">
                        <strong>✅ Key Principle:</strong> In the endgame, the king should be as active as possible. Bring it toward the center and use it to help your pawns advance.
                    </div>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Pawn Promotion</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        Getting a pawn to the 8th rank (1st rank for Black) promotes it to a queen, rook, bishop, or knight. 
                        This is often the key to winning endgames. A passed pawn is a powerful weapon!
                    </p>
                    <p style="color: #6b7280; font-size: 0.9rem; font-style: italic; margin: 1rem 0;">
                        Most of the time, you'll promote to a queen (the most powerful piece). Only promote to a different piece 
                        if it helps avoid stalemate or creates a specific tactical pattern.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Basic Endgame Principles</h3>
                    <div style="background: #f9fafb; padding: 1.5rem; border-radius: 0.75rem; margin: 1rem 0;">
                        <p style="margin: 0.75rem 0;"><strong>King and Pawn vs King:</strong> Learn to promote pawns and stop promotion. The concept of "opposition" is crucial here.</p>
                        <p style="margin: 0.75rem 0;"><strong>Rook Endgames:</strong> Activate your rook, put it behind passed pawns (yours or your opponent's). Rook endgames are the most common type.</p>
                        <p style="margin: 0.75rem 0;"><strong>Bishop vs Knight:</strong> Bishops are usually better in open positions with pawns on both sides. Knights are better in closed positions.</p>
                        <p style="margin: 0.75rem 0;"><strong>Opposition:</strong> A key concept in king and pawn endgames. When kings face each other with one square between them, the player who doesn't have to move has the opposition.</p>
                    </div>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Key Endgame Techniques</h3>
                    <ul style="color: #374151; margin: 1rem 0; padding-left: 1.5rem;">
                        <li><strong>Zugzwang:</strong> Forcing your opponent to make a bad move because any move worsens their position</li>
                        <li><strong>Triangulation:</strong> Using the king to lose a tempo and force zugzwang</li>
                        <li><strong>Pawn Breakthrough:</strong> Creating a passed pawn by sacrificing pawns</li>
                        <li><strong>Cutting Off the King:</strong> Using a rook to limit the enemy king's movement</li>
                        <li><strong>Lucena Position:</strong> A winning technique in rook and pawn endgames</li>
                        <li><strong>Philidor Position:</strong> A drawing technique in rook and pawn endgames</li>
                    </ul>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Common Endgame Mistakes</h3>
                    <ul style="color: #374151; margin: 1rem 0; padding-left: 1.5rem;">
                        <li>Not activating the king (keeping it passive)</li>
                        <li>Moving pawns too quickly (pawn moves can't be taken back!)</li>
                        <li>Not calculating accurately (endgames require precise calculation)</li>
                        <li>Giving up too easily (many endgames are drawn but look lost)</li>
                        <li>Not understanding basic checkmate patterns</li>
                    </ul>
                    
                    <div style="background: #fef3c7; border-left: 4px solid #f59e0b; padding: 1rem; margin: 1.5rem 0; border-radius: 0.5rem;">
                        <strong>💡 Study Tip:</strong> Endgames require precise calculation. Practice basic endgame positions like 
                        King and Pawn vs King, and Rook and Pawn vs Rook. These patterns appear frequently in actual games.
                    </div>
                </div>
            `,
            images: [], // Images should be uploaded to Firebase Storage
            youtubeLinks: ['https://youtu.be/hSguKl6soPg'], // Add YouTube video URLs here - Search "chess endgame fundamentals" on YouTube and add video URLs
            authorId: 'system',
            authorName: 'Chess Academy',
            tags: ['endgame', 'king activity', 'pawn promotion', 'technique'],
            difficulty: 'intermediate'
        },
        {
            title: 'Time Management: Using Your Clock Wisely',
            category: 'strategy',
            content: `
                <div style="max-width: 800px; margin: 0 auto; line-height: 1.8;">
                    <h2 style="color: #1f2937; font-size: 2rem; margin-bottom: 1rem; border-bottom: 3px solid #9333ea; padding-bottom: 0.5rem;">Using Your Time Wisely</h2>
                    <p style="font-size: 1.1rem; color: #374151; margin-bottom: 1.5rem;">
                        Time management is crucial in chess, especially in timed games. Many games are lost not because of bad moves, 
                        but because of poor time management. Learning to use your time effectively is a skill that improves with practice.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Opening Phase</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        Don't spend too much time in the opening. If you know the opening, play quickly. Save time for the middlegame and endgame 
                        where critical decisions are made. However, if your opponent plays something unexpected, it's okay to spend a bit more time.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Critical Positions</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">Spend more time on critical positions where one mistake can lose the game. Look for:</p>
                    <ul style="color: #374151; margin: 1rem 0; padding-left: 1.5rem;">
                        <li>Tactical opportunities (forks, pins, skewers)</li>
                        <li>Key decision points (should I trade pieces? Should I attack?)</li>
                        <li>Complex calculations (multiple move sequences)</li>
                        <li>Plans that change the character of the game</li>
                        <li>Defensive moves when under attack</li>
                    </ul>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Time-Saving Tips</h3>
                    <div style="background: #f9fafb; padding: 1.5rem; border-radius: 0.75rem; margin: 1rem 0;">
                        <p style="margin: 0.75rem 0;"><strong>Calculate efficiently:</strong> Don't recalculate variations you've already considered.</p>
                        <p style="margin: 0.75rem 0;"><strong>Use your opponent's time:</strong> Think during your opponent's turn, not just your own.</p>
                        <p style="margin: 0.75rem 0;"><strong>Have a plan:</strong> Don't think aimlessly. Know what you're trying to achieve.</p>
                        <p style="margin: 0.75rem 0;"><strong>Trust your intuition:</strong> In familiar positions, your first instinct is often correct.</p>
                        <p style="margin: 0.75rem 0;"><strong>Don't overthink:</strong> Sometimes a good move is better than a perfect move that takes too long.</p>
                    </div>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Time Pressure</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">When you're short on time (less than 5 minutes in a standard game):</p>
                    <ul style="color: #374151; margin: 1rem 0; padding-left: 1.5rem;">
                        <li>Make safe moves (avoid risky complications)</li>
                        <li>Avoid complex calculations (they take too long)</li>
                        <li>Simplify the position (trade pieces if possible)</li>
                        <li>Trust your intuition (first move that looks good)</li>
                        <li>Keep your king safe (don't take unnecessary risks)</li>
                        <li>Make moves quickly but not carelessly</li>
                    </ul>
                    
                    <div style="background: #fee2e2; border-left: 4px solid #ef4444; padding: 1rem; margin: 1.5rem 0; border-radius: 0.5rem;">
                        <strong>⚠️ Warning:</strong> Time pressure causes mistakes. Try to avoid getting into time trouble. 
                        It's better to have a slightly worse position with time than a better position with no time.
                    </div>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Practice Time Management</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        Play timed games regularly to practice time management. Start with longer time controls and gradually 
                        reduce them. Online chess platforms offer various time controls - use them to improve your time management skills.
                    </p>
                </div>
            `,
            images: [], // Images should be uploaded to Firebase Storage
            youtubeLinks: ['https://youtube.com/shorts/RDFlYFHk8ps'],
            authorId: 'system',
            authorName: 'Chess Academy',
            tags: ['strategy', 'time management', 'tips', 'tournament'],
            difficulty: 'intermediate'
        },
        {
            title: 'Chess Notation: The Language of Chess',
            category: 'basics',
            content: `
                <div style="max-width: 800px; margin: 0 auto; line-height: 1.8;">
                    <h2 style="color: #1f2937; font-size: 2rem; margin-bottom: 1rem; border-bottom: 3px solid #9333ea; padding-bottom: 0.5rem;">Understanding Chess Notation</h2>
                    <p style="font-size: 1.1rem; color: #374151; margin-bottom: 1.5rem;">
                        Chess notation is a system for recording chess moves. The most common system is <strong>algebraic notation</strong>. 
                        Learning notation is essential for studying chess, analyzing games, and communicating with other players.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">How It Works</h3>
                    <p style="color: #374151; margin-bottom: 1rem;">
                        The board has <strong>files</strong> (columns) labeled a-h from left to right, and <strong>ranks</strong> (rows) numbered 1-8 from bottom to top. 
                        Each square has a unique name like e4 or d5. The square name combines the file letter and rank number.
                    </p>
                    <div style="background: #dbeafe; padding: 1rem; border-radius: 0.5rem; margin: 1rem 0;">
                        <strong>Example:</strong> e4 means the square on the e-file (5th column) and 4th rank. This is one of the most popular opening moves!
                    </div>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Piece Notation</h3>
                    <div style="background: #f9fafb; padding: 1.5rem; border-radius: 0.75rem; margin: 1rem 0;">
                        <p style="margin: 0.75rem 0;"><strong>K</strong> = King</p>
                        <p style="margin: 0.75rem 0;"><strong>Q</strong> = Queen</p>
                        <p style="margin: 0.75rem 0;"><strong>R</strong> = Rook</p>
                        <p style="margin: 0.75rem 0;"><strong>B</strong> = Bishop</p>
                        <p style="margin: 0.75rem 0;"><strong>N</strong> = Knight (K is already used for King)</p>
                        <p style="margin: 0.75rem 0;"><strong>P</strong> = Pawn (usually omitted in notation)</p>
                    </div>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Special Symbols</h3>
                    <div style="background: #f9fafb; padding: 1.5rem; border-radius: 0.75rem; margin: 1rem 0;">
                        <p style="margin: 0.75rem 0;"><strong>x</strong> = capture (e.g., Nxe5 means knight captures on e5)</p>
                        <p style="margin: 0.75rem 0;"><strong>+</strong> = check (e.g., Qh5+ means queen to h5 with check)</p>
                        <p style="margin: 0.75rem 0;"><strong>#</strong> = checkmate (e.g., Qh5# means checkmate)</p>
                        <p style="margin: 0.75rem 0;"><strong>0-0</strong> = kingside castling</p>
                        <p style="margin: 0.75rem 0;"><strong>0-0-0</strong> = queenside castling</p>
                        <p style="margin: 0.75rem 0;"><strong>=</strong> = promotion (e.g., e8=Q means pawn promotes to queen)</p>
                        <p style="margin: 0.75rem 0;"><strong>!</strong> = good move</p>
                        <p style="margin: 0.75rem 0;"><strong>?</strong> = bad move</p>
                    </div>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Example Game</h3>
                    <div style="background: #fef3c7; padding: 1.5rem; border-radius: 0.75rem; margin: 1rem 0; font-family: monospace;">
                        <p style="margin: 0.5rem 0;"><strong>1.e4 e5</strong> - Both players control the center</p>
                        <p style="margin: 0.5rem 0;"><strong>2.Nf3 Nc6</strong> - Knights develop toward the center</p>
                        <p style="margin: 0.5rem 0;"><strong>3.Bb5 a6</strong> - Ruy Lopez opening begins</p>
                        <p style="margin: 0.5rem 0;"><strong>4.Ba4 Nf6</strong> - Bishop retreats, knight develops</p>
                        <p style="margin: 0.5rem 0;"><strong>5.0-0 Be7</strong> - White castles, bishop develops</p>
                    </div>
                    <p style="color: #6b7280; font-size: 0.9rem; font-style: italic; margin: 1rem 0;">
                        This is the start of the famous Ruy Lopez opening, one of the most analyzed openings in chess.
                    </p>
                    
                    <h3 style="color: #4b5563; font-size: 1.5rem; margin-top: 2rem; margin-bottom: 1rem;">Why Learn Notation?</h3>
                    <ul style="color: #374151; margin: 1rem 0; padding-left: 1.5rem;">
                        <li><strong>Record your games</strong> for analysis and improvement</li>
                        <li><strong>Study master games</strong> from chess books and databases</li>
                        <li><strong>Share positions</strong> with other players and coaches</li>
                        <li><strong>Use chess databases</strong> and engines for analysis</li>
                        <li><strong>Follow live games</strong> and tournaments</li>
                        <li><strong>Learn from annotated games</strong> in chess literature</li>
                    </ul>
                    
                    <div style="background: #dcfce7; border-left: 4px solid #10b981; padding: 1rem; margin: 1.5rem 0; border-radius: 0.5rem;">
                        <strong>✅ Practice:</strong> Try writing down the moves of your next game. This will help you learn notation quickly 
                        and improve your ability to visualize the board.
                    </div>
                </div>
            `,
            images: [], // Images should be uploaded to Firebase Storage
            youtubeLinks: ['https://youtube.com/shorts/0mnGph_sz7Y'],
            authorId: 'system',
            authorName: 'Chess Academy',
            tags: ['notation', 'basics', 'learning', 'recording'],
            difficulty: 'beginner'
        }
    ];
};

/**
 * Seed sample chess articles to the database
 */
export const seedChesspediaToDatabase = async (
    serviceProviderId: string,
    userId: string,
    userName: string,
    onProgress?: (current: number, total: number) => void
): Promise<{ success: boolean; count: number }> => {
    try {
        const articles = generateSampleChessArticles();
        const total = articles.length;
        let count = 0;
        
        // Check existing articles to avoid duplicates
        const { getChessArticles } = await import('../services/chesspediaService');
        const existingArticles = await getChessArticles(serviceProviderId);
        const existingTitles = new Set(existingArticles.map(a => a.title));
        
        for (const article of articles) {
            // Skip if article with same title already exists
            if (existingTitles.has(article.title)) {
                if (onProgress) onProgress(count + 1, total);
                continue;
            }
            
            await createChessArticle(serviceProviderId, {
                ...article,
                authorId: userId,
                authorName: userName
            });
            
            count++;
            if (onProgress) onProgress(count, total);
        }
        
        return { success: true, count };
    } catch (error) {
        console.error('Error seeding chesspedia:', error);
        return { success: false, count: 0 };
    }
};
